export type DeepEntries<T> =
  T extends Record<string, unknown>
    ? { [P in keyof T]: [P, DeepEntries<T[P]>] }[keyof T][]
    : T;

export type Entries<T extends Record<string, unknown>> = {
  [P in keyof T]: [P, T[P]];
}[keyof T][];

export const objectEntries = <T extends Record<string, unknown>>(
  obj: T,
): Entries<T> => Object.entries(obj) as Entries<T>;

export const sortedDeepEntries = <T extends Record<string, unknown>>(
  obj: T,
  maxDepth = 10,
): DeepEntries<T> => _sortedDeepEntries(obj, 0, maxDepth);

/**
 * Used internally to recursively get sorted entries up to a certain max depth
 * @param obj
 * @param depth
 * @param maxDepth
 * @returns
 */
const _sortedDeepEntries = <T extends Record<string, unknown>>(
  obj: T,
  depth = 0,
  maxDepth = 10,
): DeepEntries<T> => {
  const keys = Object.keys(obj);
  keys.sort();
  return keys.map((key) => {
    const value = obj[key];
    if (depth < maxDepth) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return [
          key,
          _sortedDeepEntries(
            value as Record<string, unknown>,
            depth + 1,
            maxDepth,
          ),
        ] as [string, unknown];
      } else if (Array.isArray(value)) {
        return [
          key,
          value.map((item) =>
            typeof item === "object" && item !== null
              ? _sortedDeepEntries(
                  item as Record<string, unknown>,
                  depth + 1,
                  maxDepth,
                )
              : item,
          ),
        ] as [string, unknown];
      }
    }
    return [key, value];
  }) as DeepEntries<T>;
};

type SerializeDate<T> = T extends Date
  ? string
  : T extends Array<infer U>
    ? SerializeDate<U>[]
    : T extends Record<string, unknown>
      ? { [K in keyof T]: SerializeDate<T[K]> }
      : T;

/**
 * Very Fast™ function to convert Date instances to ISO strings, while leaving all other values unchanged. Works recursively on objects and arrays. Don't use this when exceeding >20000 nodes in an object/ array, as it will be faster to use a more naive Object.entries based approach (V8 GC HATES this one little trick)
 *
 * @param value anything
 * @returns the same value, but with any Date instances converted to ISO strings. Works recursively on objects and arrays.
 */
export function serializeDate<T>(value: T): SerializeDate<T> {
  if (value === null || typeof value !== "object")
    return value as SerializeDate<T>;

  if (value instanceof Date) {
    return value.toISOString() as SerializeDate<T>;
  }

  if (Array.isArray(value)) {
    let changed = false;
    const len = value.length;
    const result = new Array(len);

    for (let i = 0; i < len; i++) {
      const original = value[i];
      const transformed = serializeDate(original);

      if (transformed !== original) changed = true;
      result[i] = transformed;
    }

    return (changed ? result : value) as SerializeDate<T>;
  }

  let changed = false;
  let result: Record<string, any> | null = null;

  for (const key in value) {
    if (!Object.hasOwn(value, key)) continue;

    const original = value[key];
    const transformed = serializeDate(original);

    if (transformed !== original) {
      if (!changed) {
        changed = true;

        // clone WITHOUT spread (faster, more stable)
        result = Object.create(Object.getPrototypeOf(value));
        for (const k in value) {
          if (Object.hasOwn(value, k)) {
            (result as Record<string, any>)[k] = value[k];
          }
        }
      }

      (result as Record<string, any>)[key] = transformed;
    }
  }

  return (changed ? result : value) as SerializeDate<T>;
}
